import { Injectable } from '@angular/core';
import type {
  EstructuraPaginaModyo,
  MetaPaginaModyo,
  ComponenteModyo,
  MetadatosSeoModyo,
  ConfiguracionPaginaModyo,
  AnalyticsPaginaModyo,
} from '@core/interfaces/pagina-modyo.interface';
import type { ModyoEntry, ModyoReference, ModyoResponse } from '@core/interfaces/modyo';
import type { ModyoEntryResponse } from '@core/interfaces/modyo-entry-response.interface';

@Injectable({ providedIn: 'root' })
export class ServicioTransformadorModyo {
  public transformarPagina(entry: ModyoEntryResponse): EstructuraPaginaModyo {
    return {
      meta: this.extraerMeta(entry),
      componentes: this.extraerComponentes(entry),
      seo: this.extraerSeo(entry),
      configuracion: this.extraerConfiguracion(entry),
      analytics: this.extraerAnalytics(entry),
    };
  }

  public validarEntry(entry: unknown): entry is ModyoEntry<unknown> {
    return (
      typeof entry === 'object' &&
      entry !== null &&
      'meta' in entry &&
      'fields' in entry &&
      typeof (entry as { meta: unknown }).meta === 'object' &&
      (entry as { meta: unknown }).meta !== null &&
      'uuid' in (entry as { meta: { uuid: unknown } }).meta &&
      'type' in (entry as { meta: { type: unknown } }).meta
    );
  }

  public esReferencia(field: unknown): field is ModyoReference {
    return typeof field === 'object' && field !== null && 'uuid' in field && 'content_type' in field;
  }

  public esVacio(field: unknown): boolean {
    const EMPTY_OBJECT_LENGTH = 0;
    return typeof field === 'object' && field !== null && Object.keys(field).length === EMPTY_OBJECT_LENGTH;
  }

  public extraerReferencias(obj: unknown): ModyoReference[] {
    const references: ModyoReference[] = [];

    const traverse = (value: unknown): void => {
      if (this.esReferencia(value)) {
        references.push(value);
      } else if (Array.isArray(value)) {
        value.forEach(traverse);
      } else if (typeof value === 'object' && value !== null) {
        Object.values(value).forEach(traverse);
      }
    };

    traverse(obj);
    return references;
  }

  public transformarAEntry<T>(response: ModyoResponse<T>): ModyoEntry<T> {
    return {
      meta: response.meta,
      fields: response.fields,
    };
  }

  public transformarAEntries<T>(responses: ModyoResponse<T>[]): ModyoEntry<T>[] {
    return responses.map((response) => this.transformarAEntry(response));
  }

  public crearMapaEntries<T>(entries: ModyoEntry<T>[]): Map<string, ModyoEntry<T>> {
    return new Map(entries.map((entry) => [entry.meta.uuid, entry]));
  }

  public resolverReferencia<T>(
    reference: ModyoReference,
    entryMap: Map<string, ModyoEntry<unknown>>,
  ): ModyoEntry<T> | null {
    return (entryMap.get(reference.uuid) as ModyoEntry<T>) || null;
  }

  public resolverReferenciasEntry<T>(entry: ModyoEntry<T>, entryMap: Map<string, ModyoEntry<unknown>>): ModyoEntry<T> {
    const resolvedFields = this.resolverFieldsRecursivo(entry.fields, entryMap) as T;

    return {
      ...entry,
      fields: resolvedFields,
    };
  }

  public desempaquetar(entry: ModyoEntry<unknown>): ModyoEntry<unknown> {
    if (entry.meta.type !== 'item') {
      return entry;
    }

    const fields = entry.fields as { index: number; elemento: unknown };

    if (this.validarEntry(fields.elemento)) {
      const elementoDesempaquetado = fields.elemento;

      const elementoFields =
        typeof elementoDesempaquetado.fields === 'object' && elementoDesempaquetado.fields !== null
          ? (elementoDesempaquetado.fields as Record<string, unknown>)
          : {};

      return {
        ...elementoDesempaquetado,
        fields: {
          ...elementoFields,
          index: fields.index,
        },
      };
    }

    return entry;
  }

  public desempaquetarLista(entries: ModyoEntry<unknown>[]): ModyoEntry<unknown>[] {
    const desempaquetados = entries.map((entry) => this.desempaquetar(entry));

    return desempaquetados.sort((a, b) => {
      const indexA = (a.fields as { index?: number }).index ?? Number.MAX_SAFE_INTEGER;
      const indexB = (b.fields as { index?: number }).index ?? Number.MAX_SAFE_INTEGER;
      return indexA - indexB;
    });
  }

  private extraerMeta(entry: ModyoEntryResponse): MetaPaginaModyo {
    return {
      uuid: entry.meta.uuid,
      version: '1.0',
      slug: entry.meta.slug,
      titulo: entry.fields.titulo ?? entry.meta.name,
      fecha_publicacion: entry.meta.published_at,
      fecha_actualizacion: entry.meta.updated_at,
    };
  }

  private extraerComponentes(entry: ModyoEntryResponse): ComponenteModyo[] {
    const componentes: ComponenteModyo[] = [];

    if (entry.fields.componentes_principales) {
      try {
        const principales = JSON.parse(entry.fields.componentes_principales) as ComponenteModyo[];
        componentes.push(...principales);
      } catch {
        /* Ignorar errores de parseo */
      }
    }

    if (entry.fields.componentes_sidebar) {
      try {
        const sidebar = JSON.parse(entry.fields.componentes_sidebar) as ComponenteModyo[];
        componentes.push({
          tipo: 'organismo',
          tipoComponente: 'sidebar',
          propiedades: {},
          hijos: sidebar,
        });
      } catch {
        /* Ignorar errores de parseo */
      }
    }

    return componentes;
  }

  private extraerSeo(entry: ModyoEntryResponse): MetadatosSeoModyo | undefined {
    const { fields } = entry;

    if (!fields.seo_titulo && !fields.seo_descripcion) {
      return undefined;
    }

    const palabrasClave = fields.seo_palabras_clave
      ? fields.seo_palabras_clave.split(',').map((k) => k.trim())
      : undefined;

    return {
      titulo: fields.seo_titulo,
      descripcion: fields.seo_descripcion,
      palabrasClave,
      imagenOg: fields.og_imagen,
      canonical: fields.canonical_url,
      robotsMeta: fields.robots_meta as MetadatosSeoModyo['robotsMeta'],
      ogTipo: fields.og_tipo as MetadatosSeoModyo['ogTipo'],
    };
  }

  private extraerConfiguracion(entry: ModyoEntryResponse): ConfiguracionPaginaModyo | undefined {
    const { fields } = entry;
    const hasNoRoles = 0;

    const rolesPermitidos = fields.roles_permitidos
      ? fields.roles_permitidos.split(',').map((r) => r.trim())
      : undefined;

    return {
      requiereAutenticacion: fields.requiere_autenticacion && fields.requiere_autenticacion.length > hasNoRoles,
      rolesPermitidos,
      estado: fields.estado as ConfiguracionPaginaModyo['estado'],
      cacheTtl: fields.cache_ttl,
      scriptsPersonalizados: fields.scripts_personalizados,
      estilosPersonalizados: fields.estilos_personalizados,
    };
  }

  private extraerAnalytics(entry: ModyoEntryResponse): AnalyticsPaginaModyo | undefined {
    const { fields } = entry;

    if (!fields.analytics_page_name && !fields.analytics_categoria) {
      return undefined;
    }

    let eventosTracking: Record<string, unknown> | undefined;
    if (fields.eventos_tracking) {
      try {
        eventosTracking = JSON.parse(fields.eventos_tracking) as Record<string, unknown>;
      } catch {
        /* Ignorar errores de parseo */
      }
    }

    return {
      pageName: fields.analytics_page_name,
      categoria: fields.analytics_categoria,
      eventosTracking,
    };
  }

  private resolverFieldsRecursivo(value: unknown, entryMap: Map<string, ModyoEntry<unknown>>): unknown {
    if (this.esReferencia(value)) {
      const resolved = this.resolverReferencia(value, entryMap);
      return resolved ? this.resolverReferenciasEntry(resolved, entryMap) : value;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.resolverFieldsRecursivo(item, entryMap));
    }

    if (typeof value === 'object' && value !== null && !this.esVacio(value)) {
      const resolved: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(value)) {
        resolved[key] = this.resolverFieldsRecursivo(val, entryMap);
      }
      return resolved;
    }

    return value;
  }
}
